import { useState } from "react";
import { Edit2, Save, X, Upload, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { toast } from "react-hot-toast";
import CategoryModal from "../CategoryModal";
import { CATEGORIES } from "../../shared/categories";
import { motion } from "framer-motion";
import DiaryHistory from "./DiaryHistory";
import { useLanguage } from "../../context/LanguageContext";

import api from "../../api/axios";

export default function ProviderProfile({ data, refreshProfile }) {
    const { t } = useLanguage();
    const [isEditing, setIsEditing] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Guard against null data
    if (!data || !data.provider) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500 italic">
                <div className="w-10 h-10 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin mb-4" />
                Sensing Resonance...
            </div>
        );
    }

    const provider = data.provider;

    // Form State (Initialized safely after guard)
    const [form, setForm] = useState({
        name: provider.name || data.name || "",
        gender: provider.gender || ""
    });

    const handleUpdateDetails = async () => {
        try {
            await api.put("/profile/details", form);

            toast.success(t('prof_update_success'));
            setIsEditing(false);
            refreshProfile();
        } catch (err) {
            toast.error(t('prof_update_fail'));
        }
    };

    const handleCategoryUpdate = async (selectedIds) => {
        try {
            await api.put("/profile/categories", { categoryIds: selectedIds });

            toast.success(t('prof_categories_success'));
            setShowCategoryModal(false);
            refreshProfile();
        } catch (err) {
            toast.error(t('prof_categories_fail'));
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error("File size must be under 10MB");
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("certificate", file);

            await api.post("/profile/certificate", formData);

            toast.success("Certificate submitted for verification. Admin will review soon.");
            refreshProfile(); // Should show PENDING state
        } catch (err) {
            console.error(err);
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const status = provider.verificationStatus || "UNVERIFIED"; // Fallback

    const renderStatusBadge = () => {
        // Certified / Verified flow hidden per request
        return null;
    }

    // Helper to get labels
    const getCategoryLabel = (id) => CATEGORIES.find(c => c.id === id)?.label || id;

    return (
        <div className="space-y-6">

            {/* 2. Personal Details */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('prof_personal_details')}</h2>
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-brand-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                            <Edit2 className="w-5 h-5" />
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <button
                                onClick={handleUpdateDetails}
                                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                            >
                                <Save className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Read Only */}
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('prof_username')}</label>
                        <div className="text-gray-900 dark:text-gray-200 p-2 bg-gray-50 dark:bg-gray-900 rounded">{provider.username}</div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('acc_email_label')}</label>
                        <div className="text-gray-900 dark:text-gray-200 p-2 bg-gray-50 dark:bg-gray-900 rounded">{data.email}</div>
                    </div>

                    {/* Editable */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('prof_full_name')}</label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                            />
                        ) : (
                            <div className="text-gray-900 dark:text-gray-200 p-2">{provider.name}</div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('prof_gender')}</label>
                        {isEditing ? (
                            <select
                                value={form.gender}
                                onChange={e => setForm({ ...form, gender: e.target.value })}
                                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                            >
                                <option value="Male">{t('prof_gender_male') || "Male"}</option>
                                <option value="Female">{t('prof_gender_female') || "Female"}</option>
                                <option value="LGBT+ Family">{t('prof_gender_lgbt') || "LGBT+ Family"}</option>
                            </select>
                        ) : (
                            <div className="text-gray-900 dark:text-gray-200 p-2">{provider.gender}</div>
                        )}
                    </div>
                </div>
            </section>

            {/* 3. Expertise (Categories) */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('prof_expertise_label')}</h2>
                    <button
                        onClick={() => setShowCategoryModal(true)}
                        className="p-2 text-brand-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Edit Expertise"
                    >
                        <Edit2 className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-wrap gap-2">
                    {provider.categories?.length > 0 ? (
                        provider.categories.map((sc) => {
                            const catId = sc.category?.id || sc.categoryId;
                            const rawLabel = sc.category?.label || getCategoryLabel(catId);
                            const catLabel = rawLabel.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                            return (
                                <span key={catId} className="px-3 py-1 bg-brand-blue/10 text-brand-blue rounded-full text-sm font-medium">
                                    {catLabel}
                                </span>
                            );
                        })
                    ) : (
                        <p className="text-gray-500 italic">{t('prof_no_expertise')}</p>
                    )}
                </div>
            </section>

            {/* Category Modal */}
            {showCategoryModal && (
                <CategoryModal
                    isOpen={true}
                    onClose={() => setShowCategoryModal(false)}
                    onSelect={handleCategoryUpdate}
                    initialSelected={provider.categories?.map(c => c.categoryId) || []}
                    hasHighRiskNeeds={false}
                    isProviderView={true}
                    isCertified={status === "VERIFIED"} // Use granular status
                />
            )}

            {/* 4. Sanctuary Journal */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <DiaryHistory limit={2} />
            </section>
        </div>
    );
}
